import { defSortedMap, SortedMap } from '@thi.ng/associative/sorted-map';
import { format } from 'date-fns/format';
import get from 'lodash/get';
import set from 'lodash/set';
import { FILTER_TYPE_STEP, NO_VALUE, UNRESOLVED } from './constants';
import {
  isAbsoluteString,
  isAbsoluteRef,
  isEndStep,
  isPath,
  isRef,
  isRelativeRef,
  isRelativeString,
  isStepRef,
  isReferenceList,
  isTransform,
} from './checks';
import type {
  AbsoluteRef,
  AbsoluteString,
  FilterMetadata,
  Next,
  Op,
  Option,
  Path,
  Reference,
  ReferenceList,
  RelativeRef,
  RelativeString,
  StepConfig,
  StepData,
  StepRef,
  TransformArguments,
  TransformDef,
} from './types';
import {
  drop,
  first,
  getAbsoluteStringPath,
  getKeys,
  getRelativeStringPath,
  mergeEntries,
} from './utils';

type ResolveOpts = {
  recursiveTransformResolution?: boolean;
  unresolvedValue?: any;
};

type ResolveObjectOpts = {
  recursive?: boolean;
  skipKeys?: string[];
  relativeTo?: string | Record<string, any>;
};

type FilterFlags = {
  isMyRecords: boolean;
  isStageRecord: boolean;
  hasVariableValue: boolean;
  tooltip?: string;
};

export type FilterOpts = {
  init?: ([string, any] | [string])[];
  vars?: [string, any][];
  type?: string;
  config?: FilterMetadata;
};

export type NextArgs = Parameters<Filter['nextSteps']>;

const ArrayInputTypes: StepConfig['input_type'][] = [
  'multiselect',
  'multiselect_one',
];

const expandStepConfig = (
  config: FilterMetadata['steps'][number],
  fullConfig: Record<string, any>
) => {
  const stack: string[] = [];
  const c = isAbsoluteString(config)
    ? get(fullConfig, getAbsoluteStringPath(config))
    : config;

  let configRef = c.config_ref;
  let result = c;

  while (configRef) {
    if (stack.includes(configRef)) {
      throw Error(
        `Filter metadata encountered a cyclic reference of config_ref properties. Cycle: ${stack.concat(configRef).join(' --> ')}`
      );
    }

    if (configRef && isAbsoluteString(configRef)) {
      stack.push(configRef);
      const otherConfig = get(fullConfig, getAbsoluteStringPath(configRef));
      configRef = otherConfig.config_ref;
      result = { ...otherConfig, ...result };
    }
  }

  return result;
};

const fillEmptyValues = (
  ...values: ([string] | [string, any])[]
): [string, any][] => {
  return Array.from(values, (x) =>
    x[1] === undefined ? [x[0], NO_VALUE] : (x as [string, any])
  );
};

export class Filter {
  private config: Record<string, any> | undefined;
  private map: SortedMap<string, any>;
  private step_configs: Map<string, StepConfig>;
  private vars: Map<string, any>;

  constructor(
    private full_config: Record<string, any>,
    opts: FilterOpts = {}
  ) {
    const { init = [], vars = [] } = opts;
    this.step_configs = new Map([
      [FILTER_TYPE_STEP, this.full_config.filter_type_step],
    ]);
    this.map = defSortedMap([[FILTER_TYPE_STEP, NO_VALUE]]);
    this.vars = new Map(vars);

    if (opts.type) {
      const config = this.full_config.filters[opts.type];
      this.selectFilter(config, init);
    }

    if (opts.config) {
      this.selectFilter(opts.config, init);
    }
  }

  private selectFilter(config: FilterMetadata, init: FilterOpts['init'] = []) {
    const { initial_steps = [], order, steps } = config;

    this.config = config;
    this.step_configs = new Map(
      Object.entries<FilterMetadata['steps'][number]>(steps).map(
        ([key, config]) => {
          const val = expandStepConfig(config, this.full_config);
          return [key, val];
        }
      )
    );
    this.map = defSortedMap(
      mergeEntries(fillEmptyValues(...initial_steps, ...init)),
      {
        compare: (a: string, b: string) => order[a] - order[b],
      }
    );
  }

  get type() {
    const value = this.map.get(FILTER_TYPE_STEP);
    return value === NO_VALUE ? null : value;
  }

  *[Symbol.iterator](): IterableIterator<[string, StepData]> {
    for (const [step, value] of this.map.entries()) {
      if (value instanceof Filter) {
        for (const [s, v] of value) {
          if (s !== FILTER_TYPE_STEP) {
            yield [s, v];
          }
        }
      } else {
        const config = this.step_configs.get(step);

        if (!config) {
          continue;
        }

        const {
          config_ref,
          ops,
          option_filter,
          option_paths,
          options: o,
          params,
          transform_options = true,
          value_transform,
          ...rest
        } = config;

        let data: Record<string, any> = {
          ops,
          options: o,
          option_filter,
          option_paths,
          params,
          value: value === NO_VALUE ? undefined : value,
        };

        if (isRef(option_filter)) {
          data.option_filter = this.resolve(option_filter, step, {
            recursiveTransformResolution: false,
          });
        }

        if (isRef(data.option_paths)) {
          data.option_paths = this.resolve(data.option_paths, step);
        }

        if (isRef(data.params)) {
          data.params = this.resolve(data.params, step);
        } else if (data.params) {
          data.params = this.resolveObject(data.params, { relativeTo: step });
        }

        if (isRef(data.options)) {
          data.options = this.resolve(data.options, step, {
            unresolvedValue: [],
          });
        } else if (isTransform(data.options)) {
          data.options = this.transform(data.options, config);
        }

        if (transform_options && data.options && data.option_paths) {
          data.options = data.options.map((x: any) =>
            getKeys(data.option_paths, x)
          );
        }

        if (Array.isArray(data.options)) {
          data.options = data.options.map((opt: any) => {
            return this.resolveObject(opt, { skipKeys: ['ops'] });
          });
        }

        data = { ...data, ...this.resolveObject(rest, { relativeTo: step }) };

        if (data.value !== NO_VALUE && value_transform) {
          const prev = this.vars.get('$');
          this.vars.set('$', data.value);
          const transformed = this.transform(value_transform, config);
          data.value = transformed;
          this.vars.set('$', prev);
        }

        yield [step, data];
      }
    }
  }

  *save({ root_filter = true } = {}): IterableIterator<any> {
    const steps = root_filter ? drop(this.map.keys()) : this.map.keys();

    if (root_filter) {
      const [[key, value]] = first(this.map.entries());
      const { variables_to_save } = this.full_config.filter_type_step ?? {};
      let vars = [...this.vars.entries()];
      if (Array.isArray(variables_to_save)) {
        vars = vars.filter((v) => variables_to_save.includes(v[0]));
      }
      yield [key, { filter_type: value, vars }];
    }

    for (const step of steps) {
      const config = this.resolveObject(this.step_configs.get(step) ?? {});
      const { save_value, variables_to_save } = config;

      const result_transform = save_value?.result_transform;

      const value = this.map.get(step);
      let resolved = isRef(save_value)
        ? this.resolve(save_value, step)
        : isTransform(save_value)
          ? this.transform(save_value, config)
          : !Array.isArray(save_value) && typeof save_value === 'object'
            ? this.resolveObject(save_value, { relativeTo: step })
            : save_value ?? value;

      if (result_transform) {
        resolved = {
          ...resolved,
          result_transform: isRef(result_transform)
            ? this.resolve(result_transform, step, {
                recursiveTransformResolution: false,
              })
            : result_transform,
        };
      }

      if (resolved?.error_response) {
        resolved = {
          ...resolved,
          error_response: this.resolveObject(resolved.error_response, {
            recursive: true,
          }),
        };
      }

      if (resolved?.body) {
        let body = isRef(resolved.body)
          ? this.resolve(resolved.body, step)
          : resolved.body;
        body = this.resolveObject(body, { relativeTo: step });
        resolved = { ...resolved, body };
      }

      if (resolved?.error_message) {
        const error_message = isRef(resolved.error_message)
          ? this.resolve(resolved.error_message, step)
          : resolved.error_message;

        resolved = { ...resolved, error_message };
      }

      let result = resolved;

      if (value instanceof Filter) {
        let vars = [...value.vars.entries()];

        if (Array.isArray(variables_to_save)) {
          vars = vars.filter((v) => variables_to_save.includes(v[0]));
        }

        result = {
          is_filter: true,
          filter_type: value.type,
          step_data: [...value.save({ root_filter: false })],
          vars,
        };
      }

      yield [step, result];
    }
  }

  private addStep(step: string, opts: { step_init?: any } = {}) {
    const config = this.step_configs.get(step);
    if (config?.is_filter) {
      const validFilters = Object.keys(this.full_config.filters)
        .map((x) => "'" + x + "'")
        .join(' | ');

      if (!opts.step_init) {
        throw Error(
          `Attempted to create a step with a nested filter but no filter name was provided.
           In the metadata, the step preceding '${step}' should specify a default value alongside this next step ({ "step": "${step}", "default": "___" }).
           Expected default values: ${validFilters}.`
        );
      }

      if (!this.full_config.filters[opts.step_init]) {
        throw Error(
          `Received a next step to create a nested filter, but '${opts.step_init}' is not a valid filter. Expected ${validFilters}`
        );
      }

      const config_vars =
        config.vars?.map<[string, any]>((v) => [
          v[0],
          isRef(v[1]) ? this.resolve(v[1], step) : v[1],
        ]) ?? [];
      const vars = mergeEntries(Array.from(this.vars.entries()), config_vars);
      const filter = new Filter(this.full_config, {
        vars,
        type: opts.step_init,
      });
      this.map.set(step, filter);
    } else {
      const v =
        opts.step_init === undefined
          ? this.getDefaultValue(step)
          : opts.step_init;
      this.map.set(step, v);
    }
  }

  private applyOps(obj: Record<string, any>, ops: Op[], step: string) {
    const config = this.step_configs.get(step)!;

    for (const op of ops) {
      const last = op[op.length - 1];
      const path = op.slice(0, op.length - 1) as Path;

      if (isStepRef(last)) {
        const v = this.resolveStep(last);
        set(obj, path, v instanceof Filter ? v.build() : v);
      } else if (isRelativeRef(last) || isAbsoluteRef(last)) {
        const v = this.resolve(last, step);
        set(obj, path, v);
      } else {
        const value = isTransform(last) ? this.transform(last, config) : last;
        set(obj, path, value);
      }
    }
  }

  private expandPath(
    ref:
      | Exclude<AbsoluteRef, AbsoluteString>
      | Exclude<RelativeRef, RelativeString>,
    relativeTo: string | Record<string, any>
  ) {
    const [src, ...path] = ref;
    const start = isAbsoluteRef(src)
      ? getAbsoluteStringPath(src)
      : getRelativeStringPath(src);
    const expanded = path.reduce<Path>((acc, x) => {
      if (isTransform(x)) {
        if (typeof relativeTo === 'string') {
          const config = this.step_configs.get(relativeTo)!;
          const transformed = this.transform(x, config);
          acc.push(transformed);
        } else {
          const transformed = this.transform(x, relativeTo);
          acc.push(transformed);
        }
      } else if (isStepRef(x)) {
        const resolved = this.resolveStep(x);
        acc.push(resolved);
      } else if (isAbsoluteRef(x) || isRelativeRef(x)) {
        const resolved = this.resolve(x, relativeTo);
        acc.push(resolved);
      } else if (Array.isArray(x)) {
        acc.push(...x);
      } else {
        acc.push(x);
      }
      return acc;
    }, []);
    return [...start, ...expanded];
  }

  private findFilterWithStep(step: string): Filter | null {
    if (this.config && Object.hasOwn(this.config.order, step)) {
      return this;
    }

    for (const val of this.map.values()) {
      if (
        val instanceof Filter &&
        val.config &&
        Object.hasOwn(val.config.order, step)
      ) {
        return val;
      }
    }

    return null;
  }

  private getDefaultValue(step: string) {
    const { input_type } = this.resolveObject(this.step_configs.get(step)!);
    const type = isRef(input_type)
      ? this.resolve(input_type, step)
      : input_type;
    return ArrayInputTypes.includes(type) ? [] : NO_VALUE;
  }

  private getStepKey(step: string) {
    if (step === '$') return step;
    return step.startsWith('$') ? step.substring(1) : step;
  }

  // 1-arity version will *not* resolve relative refs
  private resolve(x: Reference | ReferenceList, opts?: ResolveOpts): any;
  private resolve(
    x: Reference | ReferenceList,
    relativeTo: string | Record<string, any>,
    opts?: ResolveOpts
  ): any;
  private resolve(
    x: Reference | ReferenceList<any> | TransformDef,
    relativeTo: string | Record<string, any> = {},
    opts: ResolveOpts = {}
  ): any {
    const { recursiveTransformResolution = true, unresolvedValue = null } =
      opts;
    const resolvingStepRef = isStepRef(x);
    const src =
      typeof relativeTo === 'object'
        ? relativeTo
        : this.expandObject(this.step_configs.get(relativeTo)!);

    if (isTransform(x)) {
      return this.transform(x, src);
    }

    if (isReferenceList(x)) {
      for (const ref of x) {
        const result = this.resolve(ref, relativeTo, opts);
        if (result !== undefined) {
          return result;
        }
      }
    }

    let result = UNRESOLVED;
    if (isAbsoluteString(x)) {
      result = get(this.full_config, getAbsoluteStringPath(x));
    } else if (isRelativeString(x)) {
      result = get(src, getRelativeStringPath(x));
    } else if (resolvingStepRef) {
      result = this.resolveStep(x);
    } else if (isAbsoluteRef(x)) {
      result = get(this.full_config, this.expandPath(x, relativeTo));
    } else if (isRelativeRef(x)) {
      result = get(src, this.expandPath(x, relativeTo));
    }

    if (result === UNRESOLVED) {
      return unresolvedValue || x;
    }
    if (result === NO_VALUE) {
      return undefined;
    }
    if (resolvingStepRef) {
      // step references will contain objects or user entered strings - they won't ever point
      // to other sections of the metadata. We need to return here in case the user enters a
      // string in the form of a reference (@testing or @/testing).
      return result;
    }
    if (
      isRef(result) ||
      (recursiveTransformResolution && isTransform(result))
    ) {
      return this.resolve(result, relativeTo, opts);
    }
    return result;
  }

  /**
   * Resolves xf_spread properties within an object.
   */
  private expandObject(obj: Record<string, any>): Record<string, any> {
    const expanded = Object.entries(obj).reduce<Record<string, any>>(
      (acc, [key, value]) => {
        if (key.startsWith('xf_spread')) {
          const v = this.resolve(value, obj);

          if (typeof v === 'object') {
            return { ...acc, ...this.resolveObject(v) };
          }
        } else {
          acc[key] = value;
        }

        return acc;
      },
      {}
    );

    return expanded;
  }

  private resolveObject(
    obj: Record<string, any>,
    opts?: ResolveObjectOpts
  ): Record<string, any> {
    const recursive = opts?.recursive ?? false;
    const skipKeys = opts?.skipKeys ?? [];
    const src = opts?.relativeTo ?? obj;

    return Object.entries(obj).reduce<Record<string, any>>(
      (acc, [key, value]) => {
        const v = skipKeys.includes(key) ? value : this.resolve(value, src);
        if (key.startsWith('xf_spread')) {
          if (typeof v === 'object') {
            return { ...acc, ...this.resolveObject(v, { relativeTo: src }) };
          } else if (isRef(v)) {
            const resolved = this.resolve(v, src);

            if (typeof resolved === 'object') {
              const resolvedObj = this.resolveObject(resolved);
              return { ...acc, ...resolvedObj };
            }

            return acc;
          }
        }
        if (
          recursive &&
          !Array.isArray(v) &&
          v !== null &&
          typeof v === 'object'
        ) {
          return { ...acc, [key]: this.resolveObject(v, opts) };
        }
        acc[key] = v;
        return acc;
      },
      {}
    );
  }

  private resolveStep(type: StepRef) {
    const step_var = typeof type === 'string' ? type : type[0];
    const path = typeof type === 'string' ? null : type.slice(1);
    const step = this.getStepKey(step_var);
    let step_value = this.vars.get(step);

    if (step !== '$') {
      const filter = this.findFilterWithStep(step);

      if (filter) {
        step_value = filter.map.get(step);
      }
    }

    if (!Array.isArray(path)) return step_value;
    if (isPath(path)) return get(step_value, path);

    for (const p of path[0]) {
      const val = get(step_value, p);
      if (val !== undefined) return val;
    }
  }

  private resolveTransformArg(arg: any, relativeTo: Record<string, any>): any {
    let result = arg;

    if (isRef(result)) {
      result = this.resolve(result, relativeTo);
    } else if (isTransform(result)) {
      result = this.transform(result, relativeTo);
    }

    if (isRef(result) || isTransform(result)) {
      return this.resolveTransformArg(result, relativeTo);
    }

    if (result && typeof result === 'object' && !Array.isArray(result)) {
      result = this.resolveObject(result, { relativeTo });
    }

    return result;
  }

  private transform(def: TransformDef, relativeTo: Record<string, any>): any {
    const concat = (args: TransformArguments['xf_concat']) => {
      return args.reduce<any[]>((acc, x) => {
        const resolved = this.resolveTransformArg(x, relativeTo);
        if (Array.isArray(resolved)) {
          acc.push(...resolved);
        } else {
          acc.push(resolved);
        }
        return acc;
      }, []);
    };
    const join = (parts: TransformArguments['xf_join']): any => {
      const resolved = parts.map((x) => {
        return this.resolveTransformArg(x, relativeTo);
      });
      return resolved.filter(Boolean).join('');
    };
    const invert = (...args: TransformArguments['xf_invert']) => {
      const [ref, ret] = args;
      const resolved = this.resolveTransformArg(ref, relativeTo);
      const result = !resolved;

      if (result && ret) {
        return ret;
      }

      return result;
    };
    const bool = (...args: TransformArguments['xf_bool']) => {
      const [a, ret] = args;
      const resolved = this.resolveTransformArg(a, relativeTo);
      const result = Boolean(resolved);

      if (result && ret) {
        return ret;
      }

      return result;
    };
    const eq = (...args: TransformArguments['xf_eq']) => {
      const [a, b, ret] = args;
      const x = this.resolveTransformArg(a, relativeTo);
      const y = this.resolveTransformArg(b, relativeTo);
      return ret === undefined ? x === y : x === y ? ret : false;
    };
    const hoist = (a: TransformArguments['xf_hoist'][0]): any => {
      const x = this.resolveTransformArg(a, relativeTo);
      return Array.isArray(x) ? x[0] : x;
    };
    const map = (...args: TransformArguments['xf_map']) => {
      const [a, b] = args;
      const src = this.resolveTransformArg(a, relativeTo);

      if (Array.isArray(src)) {
        const prev = this.vars.get('$');
        const result = src.map((x: any): any => {
          this.vars.set('$', x);

          if (isTransform(b)) {
            return this.transform(b, relativeTo);
          }

          if (Array.isArray(b)) {
            return get(x, b);
          }

          return this.resolveObject(b, { relativeTo });
        });

        this.vars.set('$', prev);
        return result;
      }

      if (src && typeof src === 'object') {
        return this.resolveObject(b, { relativeTo });
      }

      return [];
    };
    const pick = (...args: TransformArguments['xf_pick']): any => {
      const [a, b] = args;
      const src = this.resolveTransformArg(a, relativeTo);
      return get(src, b);
    };
    const date_format = (...args: TransformArguments['xf_dateformat']) => {
      const [a, fmt] = args;
      const resolved = this.resolveTransformArg(a, relativeTo);
      const date = new Date(resolved);
      return isNaN(date.getTime()) ? '' : format(date, fmt);
    };
    const some = (...args: TransformArguments['xf_some']) => {
      const [a, b, ret] = args;
      const aValue = this.resolveTransformArg(a, relativeTo);
      const bValue = this.resolveTransformArg(b, relativeTo);

      if (!Array.isArray(aValue)) {
        return false;
      }

      const result = aValue.some((x) => x === bValue);
      return ret && result ? ret : result;
    };

    const or = (args: TransformArguments['xf_or']) => {
      if (!Array.isArray(args)) {
        return false;
      }

      for (const arg of args) {
        const resolved = this.resolveTransformArg(arg, relativeTo);

        if (Boolean(resolved)) {
          return resolved;
        }
      }

      return false;
    };

    const and = (args: TransformArguments['xf_and']) => {
      if (!Array.isArray(args)) {
        return false;
      }

      let resolved = false;

      for (const arg of args) {
        resolved = this.resolveTransformArg(arg, relativeTo);

        if (!resolved) {
          return false;
        }
      }

      return resolved;
    };

    switch (def[0]) {
      case 'xf_concat': {
        const [_xf, ...args] = def;
        return concat(args);
      }
      case 'xf_join': {
        const [_xf, ...args] = def;
        return join(args);
      }
      case 'xf_invert':
        return invert(def[1], def[2]);
      case 'xf_bool':
        return bool(def[1], def[2]);
      case 'xf_eq':
        return eq(def[1], def[2], def[3]);
      case 'xf_not_eq': {
        const res = !eq(def[1], def[2]);

        if (!res) {
          return false;
        }

        return def[3] === undefined ? true : def[3];
      }
      case 'xf_hoist':
        return hoist(def[1]);
      case 'xf_map':
        return map(def[1], def[2]);
      case 'xf_pick':
        return pick(def[1], def[2]);
      case 'xf_some':
        return some(def[1], def[2], def[3]);
      case 'xf_dateformat':
        return date_format(def[1], def[2]);
      case 'xf_or': {
        const [_xf, ...args] = def;
        return or(args);
      }
      case 'xf_and': {
        const [_xf, ...args] = def;
        return and(args);
      }
      default:
        throw Error(`transform called with unknown operator ${def[0]}`);
    }
  }

  getFlags(): FilterFlags {
    const opts: ResolveOpts = { unresolvedValue: false };
    const isMyRecords = this.resolve('@/is_my_records', opts);
    const isStageRecord = this.resolve('@/is_stage_records', opts);
    const hasVariableValue = this.resolve('@/has_variable_value', opts);
    const tooltip = this.resolve('@/tooltip', opts);

    return {
      isMyRecords,
      isStageRecord,
      hasVariableValue,
      tooltip,
    };
  }

  toString() {
    const strings: (string | string[])[] = [];

    for (const step of this.map.keys()) {
      const { description } = this.resolveObject(
        this.step_configs.get(step) ?? {}
      );
      const v = this.map.get(step);

      if (v instanceof Filter) {
        strings.push(v.toString());
      } else {
        if (description && v !== NO_VALUE) {
          if (typeof description === 'string') {
            strings.push(description);
          } else {
            const desc = this.resolve(description, step);
            if (typeof desc === 'string' || Array.isArray(desc)) {
              strings.push(desc);
            }
          }
        }
      }
    }

    return strings
      .map((x) => (Array.isArray(x) ? String(x).trim() : x))
      .join(' ');
  }

  build<T extends Record<string, any>>() {
    const result: Record<string, any> = {};

    for (const step of this.map.keys()) {
      const config = this.resolveObject(this.step_configs.get(step) ?? {}, {
        skipKeys: ['ops'],
      });
      const step_value = this.map.get(step);

      if (config && step_value !== undefined && step_value !== NO_VALUE) {
        if (config.ops) {
          const ops = isRef(config.ops)
            ? this.resolve(config.ops, step)
            : config.ops;
          if (Array.isArray(ops)) {
            this.applyOps(result, ops, step);
          }
        }

        if (config.options) {
          const options = isRef(config.options)
            ? this.resolve(config.options, step)
            : config.options;
          const option = options?.find?.(
            ({ value }: Option) => value === this.map.get(step)
          );
          const ops =
            option && isRef(option.ops)
              ? this.resolve(option.ops, option)
              : option?.ops;
          if (Array.isArray(ops)) {
            this.applyOps(result, ops, step);
          }
        }
      }
    }

    return result as T;
  }

  nextSteps(current_step: string, next_steps: Next[]) {
    if (!this.config || !Object.hasOwn(this.config.order, current_step)) {
      const filter = this.findFilterWithStep(current_step);

      if (!(filter instanceof Filter)) {
        throw Error(
          `Calling 'next' for current step ${current_step} is invalid. There is no filter in the chain that can handle this step.`
        );
      }

      filter.nextSteps(current_step, next_steps);
      return;
    }

    const current_next = [...drop(this.map.keys(current_step))];
    let prev_step = current_step;

    for (const ns of next_steps) {
      const {
        step,
        default: step_init = undefined,
        unset = false,
      } = typeof ns === 'object' ? ns : { step: ns };

      if (isEndStep(step)) {
        // end specified - remove any additional existing steps
        for (const key of drop(this.map.keys(prev_step))) {
          this.map.delete(key);
        }
        break;
      } else if (current_next.includes(step)) {
        // next step already exists
        // delete any steps until we reach the next step
        while (current_next[0] !== step) {
          this.map.delete(current_next[0]);
          current_next.shift();
        }
        current_next.shift(); // remove current step from buffer
        // clear or set value for step if necessary
        if (unset) this.map.set(step, this.getDefaultValue(step));
        if (step_init !== undefined) this.addStep(step, { step_init });
      } else if (
        current_next.some(
          (s) => this.config!.order[step] === this.config!.order[s]
        )
      ) {
        // another step exists with the same order # - replace it
        // delete until we reach the step with the same order #
        while (this.config.order[step] !== this.config.order[current_next[0]]) {
          this.map.delete(current_next[0]);
          current_next.shift();
        }

        this.map.delete(current_next[0]);
        current_next.shift();
        this.addStep(step, { step_init });
      } else {
        this.addStep(step, { step_init });
      }

      prev_step = step;
    }

    if (!isEndStep(current_step)) {
      // post process result to remove any remaining steps between current_step and next_steps[0]
      const next =
        typeof next_steps[0] == 'object' ? next_steps[0].step : next_steps[0];
      const steps = [...drop(this.map.keys(current_step))];

      while (steps.length && steps[0] !== next) {
        this.map.delete(steps[0]);
        steps.shift();
      }
    }
  }

  set(key: string, value: any) {
    if (key === FILTER_TYPE_STEP) {
      const config = this.full_config.filters[value];
      this.selectFilter(config);
      return;
    }

    const filter = this.findFilterWithStep(key);

    if (!filter) {
      throw Error(
        `Attempted to set ${value} to ${key} step but this step does not exist.`
      );
    }

    filter.map.set(key, value);
  }
}

export const defFilter = (
  full_config: Record<string, any>,
  opts?: FilterOpts
) => {
  return new Filter(full_config, opts);
};
